import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { formatINR } from '@vj/shared';
import { api, ApiClientError } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { TextField, SelectField, TextAreaField } from '../../components/ui/Field.jsx';
import { ChitTabs } from './ChitTabs.jsx';

const BONUS_LABEL = { none: 'No bonus', installments: 'installment(s) free', percent: '% bonus' };

function newScheme() {
  return {
    name: '',
    schemeType: 'rupee',
    durationMonths: '11',
    minMonthly: '0',
    bonusType: 'none',
    bonusValue: '0',
    purityId: '',
    termsText: '',
  };
}

export default function SchemesListPage() {
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(newScheme());
  const [submitError, setSubmitError] = useState(null);

  const schemesQuery = useQuery({ queryKey: ['chit', 'schemes'], queryFn: () => api.get('/chit/schemes') });
  const puritiesQuery = useQuery({ queryKey: ['masters', 'purities'], queryFn: () => api.get('/masters/purities') });

  const createMutation = useMutation({
    mutationFn: () =>
      api.post('/chit/schemes', {
        name: form.name,
        schemeType: form.schemeType,
        durationMonths: Number(form.durationMonths),
        minMonthly: Number(form.minMonthly),
        bonusType: form.schemeType === 'rupee' ? form.bonusType : 'none',
        bonusValue: form.schemeType === 'rupee' ? Number(form.bonusValue) : 0,
        purityId: form.schemeType === 'gold' && form.purityId ? Number(form.purityId) : undefined,
        termsText: form.termsText || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chit', 'schemes'] });
      setCreateOpen(false);
      setForm(newScheme());
    },
    onError: (err) => setSubmitError(err instanceof ApiClientError ? err.message : 'Could not save this scheme.'),
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, isActive }) => api.patch(`/chit/schemes/${id}/active`, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['chit', 'schemes'] }),
  });

  const canSubmit = form.name.trim().length >= 2 && Number(form.durationMonths) >= 1;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl font-semibold tracking-tight">Chit &amp; gold savings</h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus size={17} aria-hidden="true" /> New scheme
        </Button>
      </div>

      <ChitTabs />

      <div className="flex flex-col gap-3">
        {(schemesQuery.data ?? []).map((s) => (
          <div key={s.id} className={`card flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between ${s.is_active ? '' : 'opacity-60'}`}>
            <div>
              <p className="font-medium">{s.name}</p>
              <p className="text-sm text-muted">
                {s.scheme_type === 'gold' ? 'Gold accumulation' : 'Rupee-based'} · {s.duration_months} months
                {s.scheme_type === 'rupee' && s.bonus_type !== 'none' ? ` · ${s.bonus_value} ${BONUS_LABEL[s.bonus_type]}` : ''}
                {Number(s.min_monthly) > 0 ? ` · min ${formatINR(s.min_monthly)}/mo` : ''}
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              loading={toggleMutation.isPending}
              onClick={() => toggleMutation.mutate({ id: s.id, isActive: !s.is_active })}
            >
              {s.is_active ? 'Close to new enrolments' : 'Reopen'}
            </Button>
          </div>
        ))}
        {schemesQuery.data && schemesQuery.data.length === 0 ? (
          <p className="text-sm text-muted">No chit schemes yet — create one to start enrolling customers.</p>
        ) : null}
      </div>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="New chit scheme">
        <div className="flex flex-col gap-4">
          <TextField label="Name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField label="Type" value={form.schemeType} onChange={(e) => setForm((f) => ({ ...f, schemeType: e.target.value }))}>
              <option value="rupee">Rupee-based</option>
              <option value="gold">Gold accumulation</option>
            </SelectField>
            <TextField label="Duration (months)" type="number" inputMode="numeric" min="1" value={form.durationMonths} onChange={(e) => setForm((f) => ({ ...f, durationMonths: e.target.value }))} />
          </div>
          <TextField label="Minimum monthly amount (₹)" type="number" inputMode="decimal" value={form.minMonthly} onChange={(e) => setForm((f) => ({ ...f, minMonthly: e.target.value }))} />

          {form.schemeType === 'rupee' ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectField label="Maturity bonus" value={form.bonusType} onChange={(e) => setForm((f) => ({ ...f, bonusType: e.target.value }))}>
                <option value="none">None</option>
                <option value="installments">Free installment(s)</option>
                <option value="percent">Percentage of total paid</option>
              </SelectField>
              {form.bonusType !== 'none' ? (
                <TextField
                  label={form.bonusType === 'installments' ? 'Free installments' : 'Bonus %'}
                  type="number"
                  inputMode="decimal"
                  value={form.bonusValue}
                  onChange={(e) => setForm((f) => ({ ...f, bonusValue: e.target.value }))}
                />
              ) : null}
            </div>
          ) : (
            <SelectField label="Purity credited" value={form.purityId} onChange={(e) => setForm((f) => ({ ...f, purityId: e.target.value }))}>
              <option value="">Select purity</option>
              {(puritiesQuery.data ?? []).filter((p) => p.metal === 'gold').map((p) => (
                <option key={p.id} value={p.id}>{p.metal} {p.label}</option>
              ))}
            </SelectField>
          )}

          <TextAreaField label="Terms (shown on the account passbook)" value={form.termsText} onChange={(e) => setForm((f) => ({ ...f, termsText: e.target.value }))} />

          {submitError ? (
            <p role="alert" className="rounded-card border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{submitError}</p>
          ) : null}

          <div className="flex justify-end gap-3">
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button disabled={!canSubmit} loading={createMutation.isPending} onClick={() => { setSubmitError(null); createMutation.mutate(); }}>
              Save scheme
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
